import { useState, useMemo, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import Select, { StylesConfig } from 'react-select';
import PaymentOptions from '~/components/FundRequests/blocks/PaymentOptionsBlock';
import AddressOverwriteConfirmModal from '~/components/FundRequests/modals/AddressOverwriteConfirmModal';
import Panel from '~/components/Panel';
import { inputStyles } from '~/components/Vendors/styles';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import { verifyVendor } from '~/services/fund-requests';
import { Country } from 'country-state-city';

interface PayeeProps {
    isEditable: boolean;
    onVendorVerified?: () => void;
    onVerifyingChange?: (verifying: boolean) => void;
}

interface VendorAddress {
    id: string | number;
    address_label: string;
    is_primary?: boolean;
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: any;
    state_province?: string;
    country?: string;
    zip_code?: string;
}

// Define interface for react-select options
interface AddressOption {
    value: VendorAddress;
    label: string;
}

const Payee = ({ isEditable, onVendorVerified, onVerifyingChange }: PayeeProps) => {
    const { fundRequest, fundRequestDispatch } = useFundRequestContext();
    const { control, getValues, watch, setValue } = useFormContext();
    const [saving, setSaving] = useState(false);
    const [selectedAddress, setSelectedAddress] =
        useState<VendorAddress | null>(null);
    const [vendorError, setVendorError] = useState<string | null>(null);
    const [addressDiffData, setAddressDiffData] = useState<{
        vendorName: string;
        vendorId: string;
        differences: Array<{
            field: string;
            label: string;
            current: string;
            mip: string;
        }>;
        currentAddress: Record<string, string>;
        mipAddress: Record<string, string>;
    } | null>(null);

    const vendorIdVal = watch('vendor.vendor_id');

    const paymentType = watch('payment_type');

    // Get the appropriate address to display
    const addressToDisplay = useMemo(() => {
        // If an address is explicitly selected via the dropdown, use that
        if (selectedAddress) {
            return selectedAddress;
        }

        // First check if vendor_address is available directly
        if (fundRequest?.vendor_address) {
            return fundRequest.vendor_address;
        }

        // If not, look for a primary address in vendor_addresses
        if (fundRequest?.vendor?.vendor_addresses?.length > 0) {
            const primaryAddress = fundRequest.vendor.vendor_addresses.find(
                (addr: VendorAddress) => addr.is_primary === true,
            );

            // Return the primary address if found, otherwise the first address
            return primaryAddress || fundRequest.vendor.vendor_addresses[0];
        }

        // If no addresses are available
        return null;
    }, [
        selectedAddress,
        fundRequest?.vendor_address,
        fundRequest?.vendor?.vendor_addresses,
    ]);

    // Format addresses for react-select
    const addressOptions = useMemo(() => {
        if (!fundRequest?.vendor?.vendor_addresses?.length) return [];

        return fundRequest.vendor.vendor_addresses.map(
            (address: VendorAddress) => ({
                value: address,
                label: `${address.address_label} ${
                    address.is_primary ? '(Primary)' : ''
                }`,
            }),
        );
    }, [fundRequest?.vendor?.vendor_addresses]);

    // Find the default selected option
    const defaultAddressOption = useMemo(() => {
        if (!addressOptions.length) return null;

        if (fundRequest?.vendor_address) {
            return (
                addressOptions.find(
                    (option: AddressOption) =>
                        option.value.id === fundRequest.vendor_address.id,
                ) || addressOptions[0]
            );
        }

        // Find the primary address
        const primaryOption = addressOptions.find(
            (option: AddressOption) => option.value.is_primary,
        );
        return primaryOption || addressOptions[0];
    }, [addressOptions, fundRequest?.vendor_address]);

    const processVerifySuccess = (updatedFundRequest: any) => {
        setVendorError(null);
        toast.success('Vendor ID verified in MIP');
        fundRequestDispatch({
            type: 'set_fund_request',
            payload: updatedFundRequest,
        });
        if (typeof updatedFundRequest?.address_does_not_match === 'boolean') {
            setValue(
                'address_does_not_match',
                updatedFundRequest.address_does_not_match,
            );
        }
        if (onVendorVerified) {
            onVendorVerified();
        }
    };

    const beginVerifying = () => {
        setSaving(true);
        onVerifyingChange?.(true);
    };
    const endVerifying = () => {
        setSaving(false);
        onVerifyingChange?.(false);
    };

    const handleVerify = async () => {
        beginVerifying();
        setVendorError(null);
        try {
            const result = await verifyVendor(
                fundRequest.id,
                vendorIdVal,
                paymentType === 'CHECK'
                    ? getValues('address_does_not_match') ?? false
                    : undefined,
            );
            if (result?.error) {
                setVendorError(result.error);
                toast.error(`Error verifying vendor ID: ${result.error}`);
                endVerifying();
                return;
            }
            if (result?.needs_confirmation) {
                setAddressDiffData({
                    vendorName: result.vendor_name,
                    vendorId: result.vendor_id,
                    differences: result.differences,
                    currentAddress: result.current_address,
                    mipAddress: result.mip_address,
                });
                endVerifying();
                return;
            }
            processVerifySuccess(result);
            endVerifying();
        } catch (error) {
            endVerifying();
            console.error('Error verifying vendor ID:', error);
            const errorMessage = 'Error verifying vendor ID';
            setVendorError(errorMessage);
            toast.error(errorMessage);
        }
    };

    const handleConfirmOverwrite = async () => {
        if (!addressDiffData) return;
        const mipData = addressDiffData.mipAddress;
        setAddressDiffData(null);
        beginVerifying();
        setVendorError(null);
        try {
            const result = await verifyVendor(
                fundRequest.id,
                vendorIdVal,
                paymentType === 'CHECK'
                    ? getValues('address_does_not_match') ?? false
                    : undefined,
                { mip_data: mipData },
            );
            if (result?.error) {
                setVendorError(result.error);
                toast.error(`Error verifying vendor ID: ${result.error}`);
                endVerifying();
                return;
            }
            processVerifySuccess(result);
            endVerifying();
        } catch (error) {
            endVerifying();
            console.error('Error confirming address overwrite:', error);
            const errorMessage = 'Error confirming address overwrite';
            setVendorError(errorMessage);
            toast.error(errorMessage);
        }
    };

    // Clear error when vendor ID changes
    useEffect(() => {
        setVendorError(null);
    }, [vendorIdVal]);

    // Ensure form has a value for address_does_not_match when fundRequest changes
    useEffect(() => {
        setValue(
            'address_does_not_match',
            fundRequest?.address_does_not_match ?? false,
            { shouldDirty: true },
        );
    }, [fundRequest?.address_does_not_match, setValue]);

    return (
        <Panel>
            <form>
                <div className='mb-5 flex items-center justify-between gap-3'>
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Payee (Payable To)
                    </h2>
                    {fundRequest?.direct_vendor_pay_only && (
                        <span className='inline-flex shrink-0 rounded-full bg-yellow px-[16px] py-[6px] text-[14px]/[18px] font-semibold text-black'>
                            Direct Vendor Pay Only
                        </span>
                    )}
                </div>

                {/* Vendor ID */}
                <div className='form-group mb-3'>
                    <div className='flex items-end gap-3'>
                        <div className='flex-0'>
                            <label htmlFor='vefont-bold ndor.vendor_id'>
                                Vendor ID
                            </label>
                            <Controller
                                name='vendor.vendor_id'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='vendor.vendor_id'
                                        className={`${inputStyles} w-full transition-colors duration-200 ${
                                            !isEditable
                                                ? 'cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500 opacity-50'
                                                : 'bg-white text-black'
                                        }`}
                                        placeholder='Enter Vendor ID'
                                        {...field}
                                        readOnly={!isEditable} // Prevent changes
                                    />
                                )}
                            />
                        </div>

                        <button
                            className={`rounded-full border-[2px] border-warning bg-warning px-[24px] py-[11px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${
                                saving || !vendorIdVal || !isEditable
                                    ? 'cursor-not-allowed opacity-50'
                                    : 'cursor-pointer'
                            }`}
                            onClick={handleVerify}
                            disabled={saving || !vendorIdVal || !isEditable}
                        >
                            {saving ? `Verifying...` : `Verify`}
                        </button>
                    </div>
                    {vendorError && (
                        <div className='text-red-600 mt-2 text-sm'>
                            {vendorError}
                        </div>
                    )}
                </div>

                {/* Vendor Name */}
                <div className='form-group mb-3'>
                    <label className='mb-2 block font-bold'>Vendor Name</label>
                    <div>{fundRequest?.vendor?.name}</div>
                </div>

                {/* Phone */}
                <div className='form-group mb-3' style={{ maxWidth: 300 }}>
                    <label className='mb-2 block font-bold'>Phone</label>
                    <div>{fundRequest?.vendor?.phone}</div>
                </div>

                {/* Email */}
                <div className='form-group mb-3' style={{ maxWidth: 300 }}>
                    <label className='mb-2 block font-bold'>Email</label>
                    <div>{fundRequest?.vendor?.email}</div>
                </div>

                <PaymentOptions />

                <h2 className='mb-5 mt-8 text-[18px]/[24px] font-medium uppercase tracking-[1.44px] text-[#131212]'>
                    {paymentType === 'CHECK'
                        ? 'Pay By Check Address'
                        : 'Mailing Address'}
                </h2>
                {paymentType === 'CHECK' && (
                    <div className='form-group mb-4 flex items-center gap-3'>
                        <Controller
                            name='address_does_not_match'
                            control={control}
                            defaultValue={
                                fundRequest?.address_does_not_match ?? false
                            }
                            render={({ field }) => (
                                <input
                                    id='address_does_not_match'
                                    type='checkbox'
                                    checked={!!field.value}
                                    onChange={(e) =>
                                        field.onChange(e.target.checked)
                                    }
                                    disabled={!isEditable}
                                    className='h-4 w-4 cursor-pointer disabled:cursor-not-allowed'
                                />
                            )}
                        />
                        <label
                            htmlFor='address_does_not_match'
                            className='font-bold'
                        >
                            Address does not match MIP
                        </label>
                    </div>
                )}
                {fundRequest?.vendor?.vendor_addresses?.length > 1 && (
                    <div className='form-group mb-3'>
                        <label className='mb-2 block font-bold'>Address</label>
                        <Select<AddressOption>
                            menuPlacement='auto'
                            options={addressOptions}
                            defaultValue={defaultAddressOption}
                            isDisabled={!isEditable}
                            onChange={(option: AddressOption | null) =>
                                setSelectedAddress(option?.value || null)
                            }
                            className={`${!isEditable ? 'opacity-70' : ''}`}
                            styles={
                                {
                                    control: (provided) => ({
                                        ...provided,
                                        backgroundColor: !isEditable
                                            ? '#f3f4f6'
                                            : 'white',
                                        cursor: !isEditable
                                            ? 'not-allowed'
                                            : 'default',
                                    }),
                                } as StylesConfig<AddressOption, false>
                            }
                        />
                    </div>
                )}

                {addressToDisplay && addressToDisplay.is_primary === false && (
                    <div className='form-group mb-3 rounded-md border border-red bg-red/10 p-3 text-sm font-bold text-red'>
                        Warning: This is not the vendor's primary address.
                    </div>
                )}

                {/* Formatted address */}
                <div className='form-group mb-3 rounded-md border border-gray-200 bg-gray-50 p-4 leading-relaxed'>
                    <div>{addressToDisplay?.address_1 || ''}</div>
                    {addressToDisplay?.address_2 && (
                        <div>{addressToDisplay.address_2}</div>
                    )}
                    <div>
                        {addressToDisplay?.city || ''}
                        {addressToDisplay?.city &&
                        (addressToDisplay?.state?.state ||
                            addressToDisplay?.state_province ||
                            addressToDisplay?.zip_code)
                            ? ', '
                            : ''}
                        {addressToDisplay?.country &&
                        addressToDisplay.country !== 'US'
                            ? addressToDisplay?.state_province || ''
                            : addressToDisplay?.state?.state || ''}
                        {(addressToDisplay?.state?.state ||
                            addressToDisplay?.state_province) &&
                        addressToDisplay?.zip_code
                            ? ' '
                            : ''}
                        {addressToDisplay?.zip_code || ''}
                    </div>
                    <div>
                        {Country.getCountryByCode(
                            addressToDisplay?.country || 'US',
                        )?.name || 'United States'}
                    </div>
                </div>
            </form>
            <AddressOverwriteConfirmModal
                isOpen={!!addressDiffData}
                onClose={() => setAddressDiffData(null)}
                onConfirm={handleConfirmOverwrite}
                isLoading={saving}
                vendorName={addressDiffData?.vendorName}
                vendorId={addressDiffData?.vendorId}
                differences={addressDiffData?.differences ?? []}
                currentAddress={addressDiffData?.currentAddress ?? {}}
                mipAddress={addressDiffData?.mipAddress ?? {}}
            />
        </Panel>
    );
};

export default Payee;
